import { Controller } from "@hotwired/stimulus"

// The state on a waiting task's chip (task_chip_state): how long the task has
// waited, and a tone that warms as it ages — lilac while fresh, sunflower
// after a few hours, orange after a day, raspberry past two. The chip is
// cached and broadcast to every viewer, so the age is counted here, in the
// browser, and stays true without a reload. The person the task waits on
// reads "Waiting on you".
//
// Usage: data-controller="task-age" on .ref-chip__state, with since (ISO
// time), label ("Waiting on Dana Reyes") and assignee (a user id, or blank)
// values, and a label target. Inline (data-task-age-inline-value) reads as
// words in a sentence: "waiting on you", no age. Bare (data-task-age-bare-value)
// is the label alone, case kept, no age: "Waiting on you", before a colon.
const HOUR = 3_600_000
const TONES = [[4, "you"], [24, "wait"], [48, "warm"]]

export default class extends Controller {
  static targets = ["label"]
  static values = { since: String, label: String, assignee: String, inline: Boolean, bare: Boolean }

  connect() {
    this.render()
    this.timer = setInterval(() => this.render(), 60_000)
  }

  disconnect() {
    clearInterval(this.timer)
  }

  // A live redraw can connect the controller before its label is in place.
  labelTargetConnected() {
    this.render()
  }

  render() {
    const since = Date.parse(this.sinceValue)
    if (Number.isNaN(since)) return

    const hours = Math.max(0, (Date.now() - since) / HOUR)
    this.element.dataset.tone = TONES.find(([limit]) => hours < limit)?.[1] || "bad"

    const viewer = document.querySelector('meta[name="current-user-id"]')?.content
    const label = this.assigneeValue && viewer === this.assigneeValue ? "Waiting on you" : this.labelValue
    if (!this.hasLabelTarget) return
    let text = `${label} · ${this.age(hours)}`
    if (this.inlineValue) text = label.charAt(0).toLowerCase() + label.slice(1)
    else if (this.bareValue) text = label
    this.labelTarget.textContent = text
  }

  age(hours) {
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`
    if (hours < 24) return `${Math.floor(hours)}h`
    return `${Math.floor(hours / 24)}d`
  }
}
